import classNames from "classnames";
import { Icon } from "PFComponents/icon/icon";
import { Typography } from "PFComponents/typography";
import { IconName } from "PFTheme/graphics/icons";
import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useState } from "react";

import css from "./accordion.module.scss";
import { useIsContentOverflowVisible } from "./use_is_content_overflow_visible";

const getIconName = (iconSet: IconSet, isOpen: boolean): IconName => {
  switch (iconSet) {
    case "chevron":
      return isOpen ? "chevron-down" : "chevron-right";
    case "caret":
      return isOpen ? "caret-down" : "caret-right";
  }
};

export type AccordionState = "open" | "closed";
type HeaderSize = "small" | "large";
type IconSet = "chevron" | "caret";

type AccordionProps = {
  header: ReactNode;
  defaultState?: AccordionState;
  state?: AccordionState;
  onStateChange?: (state: AccordionState) => void;
  headerSize?: HeaderSize;
  iconSet?: IconSet;
  actions?: React.ReactNode;
};

const TRANSITION_DURATION = 200;

export const Accordion = ({
  header,
  defaultState = "open",
  state,
  onStateChange,
  children,
  headerSize = "large",
  iconSet = "caret",
  actions
}: PropsWithChildren<AccordionProps>): JSX.Element => {
  const [internalState, setInternalState] = useState<AccordionState>(state ?? defaultState);
  const [maxHeight, setMaxHeight] = useState(0);
  const isOverflowVisible = useIsContentOverflowVisible({ internalState, delay: TRANSITION_DURATION });

  const handleHeight = useCallback(
    (node: HTMLDivElement) => {
      setMaxHeight(node?.scrollHeight);
    },
    [children]
  );

  useEffect(() => {
    if (state) {
      setInternalState(state);
    }
  }, [state]);

  const handleStateChange = useCallback(() => {
    onStateChange && onStateChange(internalState === "open" ? "closed" : "open");
    setInternalState(internalState === "open" ? "closed" : "open");
  }, [internalState, onStateChange]);

  const isOpen = internalState === "open";

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div
          className={css.headerTitle}
          onClick={handleStateChange}
          onKeyDown={handleStateChange}
          role="button"
          tabIndex={0}
        >
          <Icon name={getIconName(iconSet, isOpen)} size="sm" />
          <Typography variant={headerSize === "large" ? "h4" : "h5"} noMargin>
            {header}
          </Typography>
        </div>
        {actions && <div>{actions}</div>}
      </div>
      <div
        ref={handleHeight}
        style={{
          maxHeight: isOpen ? maxHeight : 0,
          transitionDuration: `${TRANSITION_DURATION}ms`
        }}
        className={classNames(css.content, { [css.contentOverflowVisible]: isOverflowVisible })}
      >
        <div className={css.contentMargin}>{children}</div>
      </div>
    </div>
  );
};
